import React, { Component } from 'react';

import clonedeep from 'lodash.clonedeep';
import debounce from 'lodash.debounce';

import { FinderAPI, FinderType } from '@philips-emr/tws-components-api';
import { Expression, KeyCode } from '@philips-emr/tws-constants';
import { AwesomeComponent, ComponentType, ContainerProvider } from '@philips-emr/tws-core';
import { arrayOf, bool, element, eventListener, expression, number, object, oneOf, oneOfType, string } from '@philips-emr/tws-core/lib/component/prop-types';
import { msg } from '@philips-emr/tws-core/lib/services/message-service';
import { classNames, enumValues, isEmptyArrayOrObject, isEnumValid, isEqualShallow, isFunction, propagateProps } from '@philips-emr/tws-utils';

import { Input } from '..';

import { FinderBox, FinderResult } from './components';

import './finder.scss';

function getValueIndex(array, value, identifier) {
  return array.findIndex((item) => item[identifier] === value[identifier]);
}

const acceptedTypes = [
  ComponentType.BEHAVIOR,
  ComponentType.NAVIGATION,
];

@AwesomeComponent({ type: ComponentType.DATA_ENTRY, api: FinderAPI })
class Finder extends Component {
  state = {
    collapsed: false,
    inputValue: '',
    isEmpty: false,
    open: false,
    query: null,
  };

  static propTypes = {
    changeRecordExpression: expression,
    children: oneOfType([element, arrayOf(element)]).notEnumerable(),
    descriptionKey: string,
    emptyResultExpression: expression,
    enabled: bool,
    grouped: bool,
    minSearchLength: number,
    multiselect: bool,
    onBlur: eventListener,
    onChange: eventListener,
    onFocus: eventListener,
    parameters: object.notEnumerable(),
    placeholder: expression,
    primaryKey: string,
    queryKey: string,
    readOnly: bool,
    type: oneOf(enumValues(FinderType)),
    uuid: string,
    value: object.notEnumerable(),
  };

  static defaultProps = {
    changeRecordExpression: Expression.CHANGE_RECORD,
    descriptionKey: '',
    grouped: false,
    minSearchLength: 3,
    multiselect: false,
    parameters: {},
    placeholder: Expression.SEARCH,
    primaryKey: 'id',
    queryKey: 'query',
    type: FinderType.INLINE,
  };

  componentDidMount() {
    const { value } = this.props;

    if (!value && isEmptyArrayOrObject(value)) {
      this.applyEmptyState();
    } else {
      this.applyState();
    }
  }

  componentDidUpdate(prevProps) {
    const { value } = this.props;

    if (value === prevProps.value || isEqualShallow(value, prevProps.value)) {
        return;
    }

    this.applyState();
  }

  applyState( ) {
    const { type, value } = this.props;

    if (isEmptyArrayOrObject(value)) {
      this.applyEmptyState();
    } else if (type !== FinderType.INLINE) {
      const { descriptionKey, grouped, multiselect } = this.props;

      const element = multiselect ? (grouped ? clonedeep(value) : [...value]) : { ...value };
      const internalValue = multiselect ? (grouped ? clonedeep(value) : [...value]) : { ...value };

      const inputValue = Array.isArray(internalValue)
        ? internalValue[0][descriptionKey]
        : internalValue[descriptionKey];

      this.setState({ element, inputValue, internalValue });
    }

    this.setState({ query: null });
  }

  applyEmptyState() {
    const { grouped, multiselect, onChange } = this.props;
    const { internalValue: oldValue } = this.state;

    const element = multiselect ? (grouped ? {} : []) : {};
    const internalValue = multiselect ? (grouped ? {} : []) : {};

    this.setState({
      internalValue,
      element,
      collapsed: false,
      inputValue: '',
    }, () => {
      !isEmptyArrayOrObject(oldValue) && isFunction(onChange) && onChange({ value: internalValue, oldValue });
    });
  }

  defaultSearch = (query) => {
    this.setState({ query });
  };

  fieldSearch = (query) => {
    const { minSearchLength } = this.props;

    if (query.length >= Number(minSearchLength)) {
      this.setState({ query, open: true });
    }
  };

  search() {
    const { inputValue } = this.state;
    const { type } = this.props;

    const types = {
      [FinderType.INLINE]: this.defaultSearch,
      [FinderType.FIELD]: this.fieldSearch,
      [FinderType.DIALOG]: this.defaultSearch,
    };

    const fn = types[type];
    isFunction(fn) && fn(inputValue);
  }

  $search = debounce(this.search.bind(this), 300);

  onChangeRecordClick = () => {
    const { readOnly } = this.props;

    if (readOnly) {
      return false;
    }

    this.setState({ collapsed: false, query: '', inputValue: '' });
  };

  onInputChange = (event) => {
    if (!event.value) {
      this.applyEmptyState();
    } else {
      this.setState({ inputValue: event.value });
    }
    this.$search();
  };

  onInputFocus = () => {
    const { onFocus } = this.props;

    isFunction(onFocus) && onFocus();

    if (!this.shouldOpenOnFocus()) {
      return;
    }

    this.setState({ open: true });
  };

  onInputBlur = () => {
    const { onBlur } = this.props;

    this.setState({ open: false }, () => {
      isFunction(onBlur) && onBlur();
    });
  };

  onInputKeyDown = ({ keyCode }) => {
    const { open } = this.state;

    if (!open && keyCode === KeyCode.ARROW_DOWN) {
      this.setState({ open: true });
    }
  }

  setSingleSelectState = (element, value, query) => {
    const { type, onChange } = this.props;
    const { internalValue: oldValue } = this.state;

    const open = false;
    const collapsed = type === FinderType.INLINE;

    this.setState({ collapsed, open, inputValue: query, element, internalValue: value }, () => {
      isFunction(onChange) && onChange({ value: this.state.internalValue, oldValue });
    });
  };

  setSingleSelectValue = (item) => {
    const { descriptionKey } = this.props;
    const { element, value } = item;

    const descriptionValue = value[descriptionKey];
    this.setSingleSelectState(element, value, descriptionValue);

    // how to call onChange when multiselect?
  };

  setSingleSelectGroupedValue = (item) => {
    const { descriptionKey } = this.props;
    const { element, name, value } = item;

    const elementState = { [name]: element };
    const valueState = { [name]: value };

    const descriptionValue = value[descriptionKey];
    this.setSingleSelectState(elementState, valueState, descriptionValue);
  };

  setMultiSelectState = (element, value) => {
    this.setState({ open: true, collapsed: false, element, internalValue: value });
  };

  getMultiSelectState(elements, values, item, primaryKey) {
    let elementArray = [...elements];
    let valueArray = [...values];

    const valueIndex = getValueIndex(values, item.value, primaryKey);

    if (valueIndex < 0) {
      elementArray = elementArray.concat(item.element);
      valueArray = valueArray.concat(item.value);
    } else {
      delete elementArray[valueIndex];
      delete valueArray[valueIndex];
    }

    return {
      elements: elementArray.filter(Boolean),
      values: valueArray.filter(Boolean),
    };
  }

  setMultiSelectValue = (item) => {
    const { internalValue, element } = this.state;
    const { primaryKey } = this.props;

    const { elements, values } = this.getMultiSelectState(element, internalValue, item, primaryKey);

    this.setMultiSelectState(elements, values);
  };

  setMultiSelectGroupedValue = (item) => {
    const { primaryKey } = this.props;
    const { element, internalValue } = this.state;

    const elementArray = element[item.name] || [];
    const valueArray = internalValue[item.name] || [];

    const { elements, values } = this.getMultiSelectState(elementArray, valueArray, item, primaryKey);

    const elementState = { ...element, [item.name]: elements };
    const valueState = { ...internalValue, [item.name]: values };

    this.setMultiSelectState(elementState, valueState);
  };

  onItemSelected = (item) => {
    const { multiselect, grouped } = this.props;

    if (!multiselect && !grouped) {
      this.setSingleSelectValue(item);
    } else if (!multiselect && grouped) {
      this.setSingleSelectGroupedValue(item);
    } else if (multiselect && !grouped) {
      this.setMultiSelectValue(item);
    } else if (multiselect && grouped) {
      this.setMultiSelectGroupedValue(item);
    }
  };

  onBoxClose = () => {
    this.setState({ open: false });
  };

  onAfterFetch = (event) => {
    const { data } = event;

    this.setState({ isEmpty: isEmptyArrayOrObject(data) });
  };

  hasValue() {
    return this.state.internalValue && Object.keys(this.state.internalValue).length > 0;
  }

  shouldOpenOnFocus() {
    const { minSearchLength, type, readOnly } = this.props;

    if (readOnly || type === FinderType.INLINE) {
      return false;
    }

    return type === FinderType.DIALOG || Number(minSearchLength) === 0;
  }

  getClassNames() {
    const { collapsed, open } = this.state;
    const { readOnly, type } = this.props;

    return classNames({
      'c-finder': true,
      'c-finder--select': this.shouldOpenOnFocus(),
      'is-collapsed': collapsed,
      'is-open': open,
      'is-read-only': readOnly,
      'c-finder--field':
        type === FinderType.FIELD || type === FinderType.DIALOG,
      [`c-finder--${type}`]: isEnumValid(FinderType, type),
    });
  }

  getInputExternalAction() {
    const { type } = this.props;

    if (type === FinderType.INLINE) {
      return <button type="button" className="i-search-magnifier" />;
    }

    return null;
  }

  renderInput() {
    const { collapsed, inputValue } = this.state;
    const { enabled, readOnly, type } = this.props;

    if ((collapsed && !readOnly) || type === FinderType.FIELD) {
      return null;
    }

    return (
      <Input
        className="c-finder__search"
        enabled={enabled}
        externalAction={this.getInputExternalAction()}
        onChange={this.onInputChange}
        onFocus={this.onInputFocus}
        placeholder={msg(this.props.placeholder)}
        readOnly={readOnly}
        value={inputValue}
      />
    );
  }

  getSelectedValues() {
    const { grouped, multiselect } = this.props;
    const { internalValue } = this.state;

    if (grouped) {
      return Object.keys(internalValue || {}).reduce((acc, key) => {
        const values = multiselect ? internalValue[key] : [internalValue[key]];

        return acc.concat(values.map((item) => ({ ...item, __name: key })));
      }, []);
    }

    return [].concat(internalValue);
  }

  renderBox() {
    const { children, emptyResultExpression, enabled, parameters, placeholder, queryKey, readOnly, type } = this.props;
    const { collapsed, inputValue, isEmpty, open, query } = this.state;

    if (collapsed && !readOnly) {
      return null;
    }

    const params = {
      ...parameters,
      [queryKey]: query || '',
    };

    const inheritedProps = {
      onAfterFetch: this.onAfterFetch,
      onSelect: this.onItemSelected,
      parameters: params,
      selected: this.getSelectedValues(),
      showNotFound: false,
    };

    return (
      <FinderBox
        emptyResultExpression={emptyResultExpression}
        enabled={enabled}
        inputValue={inputValue}
        isEmpty={isEmpty}
        onBlur={this.onInputBlur}
        onChange={this.onInputChange}
        onClose={this.onBoxClose}
        onFocus={this.onInputFocus}
        onKeyDown={this.onInputKeyDown}
        open={open}
        placeholder={placeholder}
        readOnly={readOnly}
        type={type}
      >
        {propagateProps(children, inheritedProps)}
      </FinderBox>
    );
  }

  renderResult() {
    const { collapsed, element, internalValue } = this.state;
    const { changeRecordExpression, grouped, multiselect, type } = this.props;

    const shouldRender =
      type === FinderType.INLINE && this.hasValue() && collapsed;

    if (!shouldRender) {
      return null;
    }

    return (
      <FinderResult
        changeRecordExpression={msg(changeRecordExpression)}
        element={element}
        grouped={grouped}
        multiselect={multiselect}
        onChangeRecordClick={this.onChangeRecordClick}
        value={internalValue}
      />
    );
  }

  render() {
    const { uuid } = this.props;

    const Container = ContainerProvider.provide(this);

    return (
      <Container acceptedTypes={acceptedTypes} uuid={uuid} className={this.getClassNames()}>
        {this.renderInput()}
        {this.renderBox()}
        {this.renderResult()}
      </Container>
    );
  }
}

export default Finder;
